// Member roster spreadsheet: the template association admins fill in, and the
// parser that reads it back (CSV text; Excel files are converted to CSV first by
// rosterImportService). Pure, no network — see rosterImport.test.js.
//
// One row per person. Two people at the same home are two rows with the same
// street address and unit. Rows whose street address starts with "EXAMPLE" are
// the template's sample rows and are skipped.

export const ROSTER_COLUMNS = [
  { key: 'street', header: 'Street address', required: true, note: 'The home in the association, e.g. 100 Main St' },
  { key: 'unit', header: 'Unit', note: 'Condo or apartment unit, if any' },
  { key: 'city', header: 'City' },
  { key: 'state', header: 'State', note: 'Two letters, e.g. MT' },
  { key: 'zip', header: 'ZIP' },
  { key: 'first_name', header: 'First name' },
  { key: 'last_name', header: 'Last name' },
  { key: 'email', header: 'Email', note: 'Needed to send a portal invitation' },
  { key: 'phone', header: 'Phone' },
  { key: 'relationship', header: 'Relationship', note: 'Owner, Co-owner, Spouse, Tenant, Family member, Property manager or Board member (blank = Owner)' },
  { key: 'mailing_address', header: 'Mailing address (if different)' },
  { key: 'occupancy', header: 'Occupancy', note: 'Owner occupied, Rental or Vacant (blank = unknown)' },
];

const EXAMPLE_ROWS = [
  ['EXAMPLE 100 Main St', '', 'Kalispell', 'MT', '59901', 'Jane', 'Doe', 'jane.doe@example.com', '406-555-0100', 'Owner', '', 'Owner occupied'],
  ['EXAMPLE 100 Main St', '', 'Kalispell', 'MT', '59901', 'John', 'Doe', 'john.doe@example.com', '', 'Spouse', '', 'Owner occupied'],
  ['EXAMPLE 200 Lake Dr', 'B12', 'Kalispell', 'MT', '59901', 'Sam', 'Lee', '', '406-555-0101', 'Owner', 'PO Box 10, Denver, CO 80201', 'Rental'],
];

// Header spellings people actually use, normalised (lowercase, letters and digits only).
const HEADER_ALIASES = {
  street: ['streetaddress', 'street', 'address', 'propertyaddress', 'homeaddress', 'situsaddress', 'situs', 'address1', 'addressline1'],
  unit: ['unit', 'unitnumber', 'unitno', 'apt', 'apartment', 'suite', 'condounit'],
  city: ['city', 'town'],
  state: ['state', 'st'],
  zip: ['zip', 'zipcode', 'postalcode', 'postcode'],
  first_name: ['firstname', 'first', 'givenname'],
  last_name: ['lastname', 'last', 'surname', 'familyname'],
  full_name: ['name', 'fullname', 'owner', 'ownername', 'membername', 'resident', 'residentname'],
  email: ['email', 'emailaddress', 'email1', 'mail'],
  phone: ['phone', 'phonenumber', 'mobile', 'cell', 'telephone'],
  relationship: ['relationship', 'role', 'type', 'linktype', 'residenttype'],
  mailing_address: ['mailingaddressifdifferent', 'mailingaddress', 'mailing', 'owneraddress', 'ownermailingaddress'],
  occupancy: ['occupancy', 'occupancystatus', 'occupied'],
};

const RELATIONSHIPS = {
  owner: 'owner', homeowner: 'owner', primaryowner: 'owner',
  coowner: 'co_owner', jointowner: 'co_owner',
  spouse: 'spouse', partner: 'spouse', husband: 'spouse', wife: 'spouse',
  tenant: 'tenant', renter: 'tenant', lessee: 'tenant',
  familymember: 'family_member', family: 'family_member', child: 'family_member', relative: 'family_member',
  propertymanager: 'property_manager', manager: 'property_manager',
  boardmember: 'board_member', board: 'board_member', director: 'board_member',
};

const OCCUPANCY = {
  owneroccupied: 'owner_occupied', owner: 'owner_occupied', primary: 'owner_occupied', yes: 'owner_occupied',
  rental: 'rental', rented: 'rental', tenant: 'rental', shorttermrental: 'rental', str: 'rental',
  vacant: 'vacant', empty: 'vacant',
};

const squash = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const clean = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const csvCell = (value) => (/[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/** The template as CSV text (opens in Excel, Numbers and Google Sheets). */
export function rosterTemplateCsv() {
  return [ROSTER_COLUMNS.map((c) => c.header), ...EXAMPLE_ROWS]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n') + '\r\n';
}

/** RFC 4180 CSV -> array of rows. Handles quotes, escaped quotes, CRLF, a BOM, and ; or tab delimiters. */
export function parseCsv(text) {
  const src = String(text ?? '').replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] || '';
  const counts = { ',': 0, ';': 0, '\t': 0 };
  for (const ch of firstLine) if (ch in counts) counts[ch] += 1;
  const delimiter = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];

  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === delimiter) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

const STREET_WORDS = {
  AVENUE: 'AVE', STREET: 'ST', DRIVE: 'DR', COURT: 'CT', TERRACE: 'TER', LANE: 'LN', ROAD: 'RD',
  BOULEVARD: 'BLVD', PLACE: 'PL', CIRCLE: 'CIR', HIGHWAY: 'HWY', TRAIL: 'TRL', NORTH: 'N',
  SOUTH: 'S', EAST: 'E', WEST: 'W',
};

/** Matching key for a home: "100 Main Street", "100 MAIN ST." and unit "#b12" -> "100 MAIN ST|B12". */
export function homeKey(street, unit) {
  const s = String(street ?? '').toUpperCase().replace(/[`’‘]/g, "'").replace(/[.,]/g, ' ')
    .split(/\s+/).filter(Boolean).map((w) => STREET_WORDS[w] || w).join(' ');
  const u = String(unit ?? '').toUpperCase().replace(/^(UNIT|APT|STE|SUITE|#)\s*/, '').replace(/[^A-Z0-9-]/g, '');
  return `${s}|${u}`;
}

/**
 * Reads roster rows from parsed CSV. Returns
 *   { people, issues, examplesSkipped, unknownHeaders, missingHeaders }
 * where each person is { line, street, unit, city, state, zip, member_name, email,
 * phone, link_type, mailing_address, occupancy_status, key } and issues are
 * { line, message } for rows left out.
 */
export function readRoster(csvRows) {
  const [headerRow = [], ...body] = csvRows || [];
  const columnFor = {};
  const unknownHeaders = [];
  headerRow.forEach((raw, index) => {
    const h = squash(raw);
    if (!h) return;
    const key = Object.keys(HEADER_ALIASES).find((k) => HEADER_ALIASES[k].includes(h));
    if (key && columnFor[key] === undefined) columnFor[key] = index;
    else if (!key) unknownHeaders.push(clean(raw));
  });

  const missingHeaders = [];
  if (columnFor.street === undefined) missingHeaders.push('Street address');
  if (columnFor.email === undefined && columnFor.first_name === undefined && columnFor.last_name === undefined && columnFor.full_name === undefined) {
    missingHeaders.push('Email or a name column');
  }
  if (missingHeaders.length) return { people: [], issues: [], examplesSkipped: 0, unknownHeaders, missingHeaders };

  const get = (cells, key) => (columnFor[key] === undefined ? '' : clean(cells[columnFor[key]]));
  const people = [];
  const issues = [];
  const seen = new Set();
  let examplesSkipped = 0;

  body.forEach((cells, index) => {
    const line = index + 2; // spreadsheet row number (header is row 1)
    if (!cells.some((c) => clean(c))) return;
    const street = get(cells, 'street');
    if (/^example\b/i.test(street)) { examplesSkipped += 1; return; }
    if (!street) { issues.push({ line, message: 'No street address' }); return; }

    const name = clean([get(cells, 'first_name'), get(cells, 'last_name')].join(' ')) || get(cells, 'full_name');
    const email = get(cells, 'email').toLowerCase();
    if (!name && !email) { issues.push({ line, message: 'Needs a name or an email' }); return; }
    if (email && !EMAIL_RE.test(email)) { issues.push({ line, message: `"${email}" is not a valid email` }); return; }

    const relationshipRaw = get(cells, 'relationship');
    const linkType = relationshipRaw ? RELATIONSHIPS[squash(relationshipRaw)] : 'owner';
    if (!linkType) { issues.push({ line, message: `Unknown relationship "${relationshipRaw}"` }); return; }

    const occupancyRaw = get(cells, 'occupancy');
    const occupancy = occupancyRaw ? OCCUPANCY[squash(occupancyRaw)] || null : null;
    if (occupancyRaw && !occupancy) { issues.push({ line, message: `Unknown occupancy "${occupancyRaw}"` }); return; }

    const unit = get(cells, 'unit');
    const key = homeKey(street, unit);
    const personKey = `${key}|${email || name.toLowerCase()}`;
    if (seen.has(personKey)) { issues.push({ line, message: 'Same person listed twice for this home' }); return; }
    seen.add(personKey);

    const state = get(cells, 'state').toUpperCase();
    people.push({
      line,
      street,
      unit: unit || null,
      city: get(cells, 'city') || null,
      state: state || null,
      zip: get(cells, 'zip') || null,
      member_name: name || null,
      email: email || null,
      phone: get(cells, 'phone') || null,
      link_type: linkType,
      mailing_address: get(cells, 'mailing_address') || null,
      occupancy_status: occupancy,
      key,
    });
  });

  return { people, issues, examplesSkipped, unknownHeaders, missingHeaders };
}

/**
 * Splits people into existing homes (matched by homeKey) and new homes.
 * `homes` are the association's properties: { id, situs_address, unit_number }.
 * Returns { rows, matchedHomes, newHomes } where rows are ready for the
 * import_roster_rows RPC.
 */
export function planRosterImport(people, homes, defaults = {}) {
  const byKey = new Map((homes || []).map((h) => [homeKey(h.situs_address, h.unit_number), h.id]));
  const matched = new Set();
  const created = new Set();
  const rows = people.map((p) => {
    const propertyId = byKey.get(p.key) || null;
    if (propertyId) matched.add(propertyId); else created.add(p.key);
    return {
      line: p.line,
      property_id: propertyId,
      home_key: p.key,
      new_home: propertyId ? null : {
        situs_address: p.street,
        unit_number: p.unit,
        situs_city: p.city || defaults.city || null,
        situs_state: p.state || defaults.state || null,
        situs_zip: p.zip || defaults.zip || null,
        occupancy_status: p.occupancy_status || 'unknown',
      },
      person: {
        member_name: p.member_name,
        email: p.email,
        phone: p.phone,
        link_type: p.link_type,
        mailing_address: p.mailing_address,
      },
    };
  });
  return { rows, matchedHomes: matched.size, newHomes: created.size };
}
